/**
 * Fee Line Separator — dashboard port of the toolkit module
 * (homerunner-toolkit `includes/FeeLineSeparator/{Core,Settings}.php`, #1064
 * Wave 2).
 *
 * The core HomeRunner plugin's "Combine all fees" pricing option sums every
 * fee invoice item into a single "Fees:" line before any filter can see the
 * individual fees. The toolkit module re-implements that combine with an
 * exception list: one or more fees, matched by display name, are pulled back
 * onto their own line (optionally relabelled), while every other fee stays
 * combined — or, in "rent" mode, folds into the accommodation line instead
 * of emitting a combined line.
 *
 * Unlike the WP module — a sitewide plugin option, on by default with
 * "Cleaning Fee" pre-populated — this is an OPT-IN per-feed/per-widget
 * display setting: `resolveFeeLines()` returns null (feature off, quote
 * passes through unchanged) whenever the group isn't actually configured, so
 * an existing feed that never touches `feeLines` sees no behavior change.
 * See this file's `resolveFeeLines()` docblock for the full defaulting,
 * gating, and live-inherit contract; `config.ts`'s `feeLines` group just
 * declares the raw settings shape.
 */
import type { Quote } from './data'
import type { BookingWidgetConfig } from './config'
import { roundToCents } from '../../common/money'

export type RatePlan = Quote['rateplans'][number]
export type InvoiceItem = RatePlan['invoiceItems'][number]
export type InvoiceItemType = InvoiceItem['type']

/** A rateplan-shaped value the reshape can operate on. */
export interface RatePlanLike {
  invoiceItems: InvoiceItem[]
  totalRent?: number
}

/**
 * Invoice-item types this module treats as fees — the same taxonomy
 * `PriceDetailsModal` groups under "Fee(s):", so a fee can never appear on
 * its own row in the modal while also being invisible to the reshape (or
 * vice versa). Deliberately NOT the WP core plugin's `Core::DEFAULT_FEE_TYPES`
 * list (`FEE`, `CLEANING_FEE`, `EXTRA_GUESTS_FEE`) — two differences, both
 * driven by how the dashboard's own price-details modal already buckets
 * these types:
 *
 *  - `EXTRA_GUESTS_FEE` is EXCLUDED: the modal displays it under "Rent:",
 *    not "Fee(s):", so combining/separating it here would visually
 *    contradict the modal's own grouping. It is simply never touched by the
 *    reshape — stays its own row, keeps its position and type.
 *  - `SERVICE_FEE` is INCLUDED: the modal already displays it as a fee, so
 *    it must be reshape-eligible for the two to agree.
 *
 * Not operator-configurable — the taxonomy is fixed to match the modal.
 */
export const FEE_ITEM_TYPES: InvoiceItemType[] = ['CLEANING_FEE', 'FEE', 'SERVICE_FEE']

const ACCOMMODATION_TYPE: InvoiceItemType = 'ACCOMMODATION_FARE'

export interface ResolvedFeeLineConfig {
  feeTypes: InvoiceItemType[]
  /** Normalized (see `normalize()`), blank-filtered. */
  names: string[]
  matchMode: 'contains' | 'exact'
  /** Normalized original title -> display title. */
  relabel: Record<string, string>
  combinedLabel: string
  position: 'before' | 'after'
  mode: 'combined' | 'rent'
}

/**
 * Normalize a fee title for comparison: coerce to a string (a title is
 * expected to be a string, but the quote API's runtime shape can diverge
 * from its TypeScript type — a null/missing title must normalize to '',
 * never throw), trim, lowercase, collapse internal runs of whitespace to a
 * single space, and drop a trailing colon (core appends "Fees:" with a
 * colon). The collapse runs FIRST and the trailing strip is `\s`-aware:
 * non-ASCII whitespace before a trailing colon (NBSP is standard French
 * typography — 'Frais de ménage :') must not survive as a trailing space
 * the equality/contains match then silently misses. Mirrors
 * `Core::normalize()`.
 */
export function normalize(value: unknown): string {
  if (typeof value !== 'string') return ''
  let v = value.trim().toLowerCase()
  v = v.replace(/\s+/g, ' ')
  v = v.replace(/[:\s]+$/, '')
  return v
}

/**
 * Does an already-normalized fee title match any of the configured names?
 * Mirrors `Core::title_matches()`. Both `normalizedTitle` and `names` are
 * expected PRE-NORMALIZED (via `normalize()`) — `resolveFeeLines` normalizes
 * the configured names once (the same treatment its relabel map keys
 * already get), and the reshape loop normalizes each item's title once and
 * reuses it for both this match check and the relabel lookup — so this stays
 * a pure string comparison with no redundant re-normalization per item ×
 * name.
 */
export function titleMatches(normalizedTitle: string, names: string[], mode: 'contains' | 'exact' = 'contains'): boolean {
  if (normalizedTitle === '') return false

  for (const name of names) {
    if (name === '') continue
    if (mode === 'exact') {
      if (name === normalizedTitle) return true
    } else if (normalizedTitle.includes(name)) {
      return true
    }
  }
  return false
}

/**
 * Fold the non-separated fees into the accommodation line (rent mode).
 * Mirrors `Core::fold_into_accommodation()`. Returns null when there is no
 * ACCOMMODATION_FARE row to fold into — the caller then degrades to the
 * combined-line behaviour, so no fee amount is ever silently dropped.
 */
function foldIntoAccommodation(
  items: InvoiceItem[],
  separated: InvoiceItem[],
  remainder: number,
  feeTypes: InvoiceItemType[],
): InvoiceItem[] | null {
  const accIndex = items.findIndex((item) => item.type === ACCOMMODATION_TYPE)
  if (accIndex === -1) return null

  // Same runtime-divergence guard the reshape loop applies to fee amounts,
  // for the row being folded INTO: `(Number('1,250.00') || 0) + remainder`
  // would replace the real accommodation charge with just the remainder — a
  // $1,250 rent silently becoming a $100 row while the total stays $1,350
  // (#1064 Wave 3 review; the checkout/confirmation consolidation routes
  // those surfaces through this fold too). Returning null degrades to the
  // combined-line behaviour: fees combine into their own line and the
  // un-foldable accommodation row passes through untouched, rendering its
  // malformed amount loudly instead of vanishing money.
  const accAmount = Number(items[accIndex].amount)
  if (!Number.isFinite(accAmount)) return null

  const result: InvoiceItem[] = []
  let inserted = false

  items.forEach((item, i) => {
    if (i === accIndex) {
      // Always fold the RAW remainder so the breakdown reconciles to the
      // cent; adding 0 is a harmless no-op when fees exactly offset.
      result.push({ ...item, amount: accAmount + remainder })
      return
    }
    if (feeTypes.includes(item.type)) {
      if (!inserted) {
        result.push(...separated)
        inserted = true
      }
      return
    }
    result.push(item)
  })

  return result
}

/**
 * Reshape a single rateplan's invoiceItems: pull the named fee(s) onto their
 * own line and combine the remaining fees into one line (or fold them into
 * the accommodation line in "rent" mode). Mirrors `Core::reshape_rateplan()`.
 *
 * The fee block is re-inserted at the position of the first fee item, so
 * surrounding rows (accommodation, taxes, deposits) keep their order.
 * `total` / `totalTaxes` / `discount` are never touched — this is a
 * presentation change only. In "rent" mode, `totalRent` (when present and
 * numeric — a numeric string is coerced) is adjusted by the folded remainder
 * so the nightly-price-basis "rent" feature (#1064 Wave 1) and the
 * price-details modal never disagree — see the reconcile comment below for
 * why it adds to the prior value rather than re-deriving from the
 * accommodation rows.
 */
export function reshapeRatePlan<T extends RatePlanLike>(ratePlan: T, config: ResolvedFeeLineConfig): T {
  if (!ratePlan.invoiceItems || ratePlan.invoiceItems.length === 0) return ratePlan

  const { feeTypes, names, matchMode, relabel, combinedLabel, position, mode } = config
  const items = ratePlan.invoiceItems
  const separated: InvoiceItem[] = []
  let combineSum = 0
  let hasFee = false

  for (const item of items) {
    if (!feeTypes.includes(item.type)) continue

    // Runtime-divergence guard, same class as normalize()'s non-string title:
    // a non-numeric fee amount (`Number('1,250.00') → NaN`) would contribute
    // $0 to the combined line while its row is dropped by the rebuild — money
    // silently vanishing from the breakdown with `total` untouched. Pass the
    // rateplan through un-reshaped instead; the un-reshaped rows at least
    // still show what the API sent.
    const amount = Number(item.amount)
    if (!Number.isFinite(amount)) return ratePlan
    hasFee = true

    // Normalized once per item and reused for both the name match below and
    // the relabel-map lookup — previously each normalized the item's title
    // separately.
    const normTitle = normalize(item.title)

    if (titleMatches(normTitle, names, matchMode)) {
      // The typeof check makes the no-prototype-pollution invariant local
      // rather than a contract on every caller: a relabel map built by hand
      // (tests, future call sites) on a plain `{}` can resolve `normTitle ===
      // 'constructor'` to a function through the prototype chain.
      const relabelTo = relabel[normTitle]
      separated.push(typeof relabelTo === 'string' && relabelTo !== '' ? { ...item, title: relabelTo } : item)
    } else {
      combineSum += amount
    }
  }

  // No fees on this rateplan — nothing for this module to do.
  if (!hasFee) return ratePlan

  if (mode === 'rent') {
    const folded = foldIntoAccommodation(items, separated, combineSum, feeTypes)
    if (folded) {
      // A numeric-string totalRent (possible after a JSON round trip) must
      // be reconciled too, and always returned as a number.
      //
      // Reconcile = prior + folded remainder, NEVER a re-derive from the
      // post-fold ACCOMMODATION_FARE rows: Central passes the PMS `totalRent`
      // through verbatim, and its basis is wider than the ACC rows for at
      // least Hostfully and Hostaway (both count extra-guest amounts in
      // `totalRent` while emitting them as separate EXTRA_GUESTS_FEE rows —
      // `GetQuotesResponse.php` in each transformer). Summing ACC rows would
      // silently rebase the nightly "rent" price on those feeds — the exact
      // drift this reconcile exists to prevent — while adding the remainder
      // preserves whatever the PMS meant by `totalRent`.
      const priorTotalRent = ratePlan.totalRent
      const priorAsNumber = Number(priorTotalRent)
      const reconcileTotalRent = priorTotalRent != null && Number.isFinite(priorAsNumber)

      return {
        ...ratePlan,
        invoiceItems: folded,
        ...(reconcileTotalRent ? { totalRent: priorAsNumber + combineSum } : {}),
      } as T
    }
    // No accommodation row to fold into — fall through to combined-line behaviour.
  }

  // Emit the combined line whenever the non-separated fees sum to a non-zero
  // (rounded) amount. A rounded-to-zero remainder is omitted (no bare "$0.00"
  // line); a negative remainder is KEPT so the breakdown still reconciles to
  // the total. The emitted amount is the RAW sum (unrounded), matching core's
  // combine — currency formatting is left to the renderer.
  const remainderIsZero = roundToCents(combineSum) === 0
  const combined: InvoiceItem | null = remainderIsZero
    ? null
    : { title: combinedLabel, amount: combineSum, type: 'FEE' }

  const feeBlock = position === 'after'
    ? [...(combined ? [combined] : []), ...separated]
    : [...separated, ...(combined ? [combined] : [])]

  // Rebuild invoiceItems: keep non-fee rows in place, drop the original fee
  // rows, and drop the assembled fee block in where the first fee row was.
  const result: InvoiceItem[] = []
  let inserted = false
  for (const item of items) {
    if (feeTypes.includes(item.type)) {
      if (!inserted) {
        result.push(...feeBlock)
        inserted = true
      }
      continue
    }
    result.push(item)
  }

  return { ...ratePlan, invoiceItems: result } as T
}

type FeeLinesSetting = NonNullable<BookingWidgetConfig['feeLines']>

/**
 * Has the operator actually configured something here, as opposed to an
 * empty `{}` a form save could write for a panel nobody touched?
 *
 * Arms the feature ONLY on: `names` non-empty (after the SAME normalize the
 * resolver applies — a name like ':::' that normalizes to nothing must not
 * arm a config that then resolves to zero names and collapses every fee),
 * OR `combinedLabel` non-blank, OR `mode: 'rent'`. `relabel`, `matchMode`
 * and `position` are MODIFIERS and never arm it on their own:
 *
 *  - the admin form's enum Select can't be returned to "unset" once touched,
 *    so a curiosity click on Match Mode or Position must not silently turn
 *    the feature on;
 *  - a relabel-only config renames nothing that exists yet (there is no
 *    separated row to rename), so it must not collapse every other fee into
 *    a combined line as a side effect.
 *
 * A bare `mode: 'rent'` with no Names IS armed — "fold every fee into rent,
 * no exceptions" is a real, deliberate config with no other way to express
 * it. A bare `mode: 'combined'` is NOT: its option label reads like a
 * description of the default, the Select can't be un-set, and arming on it
 * made a curiosity click + autosave collapse every fee with no UI path back.
 * Treating it as unset makes "combined" the Select's escape hatch (the
 * save-path `stripBlankFeeLines` drops the bare leaf entirely; this gate is
 * the read-side belt-and-braces for blobs that dodge that strip). The
 * deliberate combine-everything config is still expressible — set the
 * Combined Line Label — and `mode: 'combined'` still arms alongside any
 * armed leaf, and alongside a non-blank relabel pair (an explicit mode next
 * to real config is no longer indistinguishable from a stray click).
 */
function isConfigured(fl: FeeLinesSetting): boolean {
  if ((fl.names ?? []).some((n) => normalize(n).length > 0)) return true
  if (fl.combinedLabel !== undefined && fl.combinedLabel.trim() !== '') return true
  if (fl.mode === 'rent') return true
  if (fl.mode === 'combined') {
    return (fl.relabel ?? []).some(
      (pair) => (pair?.from ?? '').trim() !== '' || (pair?.to ?? '').trim() !== '',
    )
  }
  return false
}

/**
 * Resolve the raw `feeLines` widget setting into the config
 * `reshapeRatePlan` consumes. This is the single point of use for every
 * default and every gating/normalization rule the feature has — `config.ts`
 * only declares the raw shape.
 *
 * - **No schema defaults on the raw setting** (feed-global-clobber rule,
 *   same pattern as `priceDisplay.showNoDatesLabel`/`noDatesLabel` in
 *   `widgets/explorer/config.ts`): every leaf here defaults INSIDE this
 *   function rather than in the zod schema, because a materialized schema
 *   default would be written by the next widget-level save and — through
 *   `getFinalSettings`' atomic-array/object merge, where the widget layer
 *   always outranks the feed-global layer — clobber a real feed-global
 *   value. `src/lib/widget-settings-prep.ts`'s `stripBlankFeeLines` is the
 *   other half of this: it strips the blank shape the admin form engine
 *   still materializes on every save, back to "absent", before it ever
 *   reaches storage.
 * - **Opt-in gate** (`isConfigured`, above): returns null — feature off,
 *   quote passes through unchanged — whenever the group isn't actually
 *   configured, so an existing feed that never touches `feeLines` sees no
 *   behavior change (unlike the WP module, sitewide and on by default).
 * - **Live-inherit**: `feeLines` is excluded from the widget-creation and
 *   feed-sync snapshots (`src/components/widget/default-settings.ts`'s
 *   `LIVE_INHERITED_BOOKING_KEYS`) so a widget that never set its own
 *   `feeLines` keeps inheriting the feed-global value LIVE rather than
 *   freezing whatever the feed said at creation/sync time.
 * - **Normalization**: `names` and `relabel`'s keys are normalized (via
 *   `normalize()`) exactly once, here, rather than per-item inside the
 *   (much hotter) reshape loop.
 */
export function resolveFeeLines(settings: Pick<BookingWidgetConfig, 'feeLines'>): ResolvedFeeLineConfig | null {
  const fl = settings.feeLines
  if (!fl || !isConfigured(fl)) return null

  const names = (fl.names ?? []).map((n) => normalize(n)).filter((n) => n.length > 0)

  // A null-prototype map: `relabel[normTitle]` is looked up in the reshape
  // loop with a fee's own title, which is arbitrary (a PMS integration's
  // free-text field, not something the operator controls). On a plain `{}`,
  // a title normalizing to "constructor" (or "__proto__" / "prototype")
  // would resolve through the prototype chain to a function object instead
  // of `undefined`, and get assigned onto `item.title`. `Object.create(null)`
  // removes the chain entirely; the explicit skip below additionally refuses
  // to ever *write* one of those keys, so a relabel config naming one of
  // them is inert rather than merely harmless-by-omission.
  const relabel: Record<string, string> = Object.create(null)
  for (const pair of fl.relabel ?? []) {
    const from = normalize(pair?.from ?? '')
    const to = (pair?.to ?? '').trim()
    if (!from || !to) continue
    if (from === '__proto__' || from === 'constructor' || from === 'prototype') continue
    relabel[from] = to
  }

  return {
    feeTypes: FEE_ITEM_TYPES,
    names,
    matchMode: fl.matchMode === 'exact' ? 'exact' : 'contains',
    relabel,
    combinedLabel: fl.combinedLabel?.trim() || 'Fees:',
    position: fl.position === 'after' ? 'after' : 'before',
    mode: fl.mode === 'rent' ? 'rent' : 'combined',
  }
}
